import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton, Button } from '@ui/button';
import { RoundedCheckbox, MultiSelect, Radio } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { useAllergies, useGetAllergies } from '../../allergies';
import { StyledFormWithImage } from '../StyledForm';
import { ApiResult } from '../../api';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { Question } from '../Question';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import {
  FieldWrapper,
  ImageContainer,
  MobileImageContainer,
  DesktopImageContainer,
  CheckboxContainer,
  MultiSelectContainer,
  ArrowContainer,
  SelectionButtonContainer,
} from './styles.js';
import {
  findAllergyInState,
  toggleFeaturedAllergy,
  getPlaceholderAllergies,
  normalize,
} from './functions';
import { schema } from './schema';

export { schema } from './schema';

export const AllergyForm = ({
  defaultValues,
  goBack,
  onSubmit,
  index,
  labelName,
  allergyQuestionSubheadline,
  allergyQuestionPrompt,
  allergiesSelectionPrompt,
  allergiesSelectionCaption,
  allergiesSearchLabel,
  placeholderAllergiesCount,
  ctaButtonText = 'Continue',
  mobileImage,
  desktopImage,
  flowType,
}) => {
  //* Hooks and form data variables
  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCartContext();
  const getAllergies = useGetAllergies();
  const allergiesList = useAllergies();

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const isSubmittingOrSuccessful =
    isSubmitting || (isSubmitted && isSubmitSuccessful);

  //* State variables
  const [view, setView] = useState('question');

  useEffect(() => {
    if (defaultValues && !isSubmittingOrSuccessful) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, isSubmittingOrSuccessful]);

  //* Selection variables
  const placeholderAllergies = getPlaceholderAllergies(
    placeholderAllergiesCount,
  );

  const featuredAllergies = normalize(
    allergiesList?.filter(item => item.featured) ?? placeholderAllergies,
  );

  const multiselectOnChange = multiselectedValues => [...multiselectedValues];

  //* Navigation variables
  const goBackWithReporter = () => {
    if (view === 'selection') {
      reporter.tag(
        onboardingBackArrowClicked(
          'pup_allergy_selection',
          cart?.customer || customer,
        ),
      );
      setView('question');
    } else {
      reporter.tag(
        onboardingBackArrowClicked('pup_allergy', cart?.customer || customer),
      );
      goBack();
    }
  };

  const handleQuestionChange = (onChange, value) => {
    onChange(value);

    if (value === 'false') {
      handleSubmit(submitForm)();
    } else if (value === 'true') {
      setView('selection');
    }
  };

  const submitForm = async data => {
    const step = view === 'selection' ? 'dog_allergies' : 'dog_allergy';

    reporter.tag(
      onboardingSubmitted(
        index,
        step,
        cart?.customer || customer,
        data,
        flowType,
      ),
    );
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  const currentPrompt =
    view === 'selection' ? allergiesSelectionPrompt : allergyQuestionPrompt;
  const currentSubheadline =
    view === 'selection'
      ? allergiesSelectionCaption
      : allergyQuestionSubheadline;

  return (
    <StyledFormWithImage onSubmit={handleSubmit(submitForm)}>
      <ImageContainer>
        <ArrowContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isSubmittingOrSuccessful}
            type="button"
            variant="primary"
          />
        </ArrowContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper
        $minHeight={'280'}
        $maxHeight={view === 'selection' ? 70 : undefined}
      >
        <FormFieldsContainer $maxWidth={391}>
          <Question
            subheadline={currentSubheadline}
            prompt={currentPrompt}
            typography={'heading4'}
            hideSubheadlineOnMobile={false}
            gap={0}
          >
            <FieldWrapper>
              {view === 'question' && (
                <Controller
                  control={control}
                  name="allergy"
                  render={({
                    field: { ref, onChange, value, ...field },
                    fieldState: { error },
                  }) => (
                    <Radio
                      {...field}
                      value={value}
                      variant="light"
                      showRadioIcon={false}
                      choices={[
                        { name: 'Yes', value: 'true' },
                        { name: 'No', value: 'false' },
                      ]}
                      legendText={labelName}
                      name="allergy"
                      error={error?.message || ''}
                      status={error?.message ? 'error' : null}
                      disabled={isSubmittingOrSuccessful}
                      onChange={newValue => {
                        if (!isSubmittingOrSuccessful) {
                          handleQuestionChange(onChange, newValue);
                        }
                      }}
                      onClick={event => {
                        if (
                          !isSubmittingOrSuccessful &&
                          event?.target?.value === value
                        ) {
                          handleQuestionChange(onChange, value);
                        }
                      }}
                    />
                  )}
                />
              )}
              {view === 'selection' && (
                <Controller
                  control={control}
                  name="allergies"
                  render={({
                    field: { onChange, value: selectedAllergies = [] },
                  }) => (
                    <>
                      <CheckboxContainer>
                        {featuredAllergies.map(allergy => (
                          <RoundedCheckbox
                            {...allergy}
                            key={allergy.value}
                            height={50}
                            variant="light"
                            checked={
                              !!findAllergyInState(allergy, selectedAllergies)
                            }
                            onChange={() =>
                              onChange(
                                toggleFeaturedAllergy(
                                  allergy,
                                  selectedAllergies,
                                ),
                              )
                            }
                            showIcon={
                              findAllergyInState(allergy, selectedAllergies)
                                ? 'check'
                                : null
                            }
                            label={allergy.label}
                          />
                        ))}
                      </CheckboxContainer>

                      <MultiSelectContainer>
                        <MultiSelect
                          name="other allergies"
                          selectedValues={selectedAllergies}
                          featuredValues={featuredAllergies}
                          values={getAllergies}
                          onChange={values =>
                            onChange(multiselectOnChange(values))
                          }
                          searchLabel={allergiesSearchLabel}
                        />
                      </MultiSelectContainer>
                    </>
                  )}
                />
              )}
            </FieldWrapper>
          </Question>

          {view === 'selection' && (
            <SelectionButtonContainer>
              <Button
                type="submit"
                aria-label={ctaButtonText}
                disabled={isSubmittingOrSuccessful}
                variant="yellow"
                isLoading={isSubmittingOrSuccessful}
                horizontalPadding={32}
              >
                {ctaButtonText}
              </Button>
            </SelectionButtonContainer>
          )}
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

AllergyForm.displayName = 'AllergyForm';

AllergyForm.propTypes = {
  defaultValues: PropTypes.shape({
    allergy: PropTypes.string,
    allergies: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  allergyQuestionSubheadline: PropTypes.string.isRequired,
  allergyQuestionPrompt: PropTypes.string.isRequired,
  ctaButtonText: PropTypes.string,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  allergiesSelectionPrompt: PropTypes.string.isRequired,
  allergiesSelectionCaption: PropTypes.string,
  allergiesSearchLabel: PropTypes.string.isRequired,
  placeholderAllergiesCount: PropTypes.number.isRequired,
  flowType: PropTypes.string,
};
